import { z } from 'zod';
/** Matches the Prisma FacilityType enum. */
export const FacilityTypeSchema = z.enum([
    'LTC_HOME',
    'RETIREMENT_RESIDENCE',
    'HOSPITAL',
    'OTHER',
]);
/** Matches the Prisma FacilityStatus enum. */
export const FacilityStatusSchema = z.enum([
    'PROSPECT',
    'ACTIVE',
    'SUSPENDED',
    'TERMINATED',
]);
/** Matches the Prisma InvoiceCadence enum (Q9.A). */
export const InvoiceCadenceSchema = z.enum(['WEEKLY', 'BIWEEKLY', 'MONTHLY']);
/** Matches the Prisma DunningProfile enum (Q9.A). */
export const DunningProfileSchema = z.enum(['STANDARD', 'LENIENT', 'STRICT']);
/** Phase 14b-2-4 — credit-hold status. Mirrors Prisma `CreditHoldStatus`; stamped on
 *  the facility by 10e dunning; a list-column + filter for the admin facility list.
 *  (Relocated from admin.schema.ts 2026-09-21 — Convention 06 per-resource split.) */
export const CreditHoldStatusSchema = z.enum(['NONE', 'WARNED', 'ACTIVE']);
/** Phase 14b-2-4 — derived from facility_contract rows: `active` = a row with
 *  effective_to IS NULL; `expired` = only historical contracts; `none` = never
 *  contracted. (Relocated from admin.schema.ts 2026-09-21 — Convention 06.) */
export const FacilityContractStateSchema = z.enum(['active', 'expired', 'none']);
/** POST /api/v1/facilities/inquire body.
 *
 *  Public lead-capture form from the marketing site /for-facilities page.
 *  Creates a Facility row in PROSPECT status; NO User account is created
 *  (Q7 + Phase 7 Track A scope — facility users are invited by the
 *  operator after contracting).
 *
 *  Anti-spam mitigations layered at this surface:
 *  1. `_botField` honeypot — humans never see / fill it; bots that fill
 *     all visible fields fill the hidden one too. Service drops requests
 *     with a non-empty value silently (returns the success response so
 *     bots can't probe for the honeypot).
 *  2. Per-IP rate limit via @nestjs/throttler on the controller.
 *
 *  Operator triages the lead from the admin queue and either promotes to
 *  ACTIVE (with contract + portal invites) or terminates.
 */
export const FacilityInquiryRequestSchema = z.object({
    // Honeypot — UI hides this field; humans never fill it.
    // audit medium — `.max(200).optional()` (NOT `.max(0)`), matching the working
    // contact/application forms. `.max(0)` rejected any filled honeypot with a 400
    // at the ZodValidationPipe BEFORE the service honeypot ran, so the silent-drop
    // audit path was dead code and a bot got a distinguishable 400.
    _botField: z.string().max(200).optional(),
    /** Cloudflare Turnstile token — human-verification; checked by TurnstileGuard.
     *  Optional so dev/e2e (no site key) still submit; enforced when a key is set. */
    turnstileToken: z.string().max(4096).optional(),
    // Facility identity
    // Audit H16 (2026-07-28): human-readable messages on the user-facing required
    // fields — the raw Zod defaults ("String must contain at least 1 character(s)",
    // "Invalid email") leaked verbatim onto the public /for-facilities form.
    name: z.string().min(1, 'Facility name is required').max(200).trim(),
    type: FacilityTypeSchema,
    // Address — optional at inquiry time; required to activate.
    addressLine1: z.string().max(200).trim().nullable().optional(),
    city: z.string().max(80).trim().nullable().optional(),
    postalCode: z.string().max(10).trim().nullable().optional(),
    // Primary contact — required so Terry has a person + channel to follow up.
    primaryContactName: z.string().min(1, 'Contact name is required').max(120).trim(),
    primaryContactRole: z.string().min(1, 'Contact role is required').max(80).trim(),
    primaryContactEmail: z
        .string()
        .email('Please enter a valid email address')
        .max(254)
        .toLowerCase(),
    primaryContactPhone: z
        .string()
        .min(10, 'Please enter a valid phone number')
        .max(30)
        .trim(),
    // Lead context — all optional.
    estimatedBeds: z.coerce.number().int().positive().max(2000).nullable().optional(),
    estimatedShiftsPerWeek: z.coerce.number().int().positive().max(500).nullable().optional(),
    bestTimeToReach: z.string().max(120).trim().nullable().optional(),
    message: z.string().max(2000).trim().nullable().optional(),
});
export const FacilityInquiryResponseSchema = z.object({
    ok: z.literal(true),
    /** Stable reference for the created facility row. UI can echo this to
     *  the visitor; operator uses it as the audit hook. Null when the
     *  honeypot was triggered (we still return ok=true to avoid leaking
     *  the honeypot's existence to bots). */
    reference: z.string().uuid().nullable(),
});
//# sourceMappingURL=inquiry.schema.js.map